'use client';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import type { LiveEvent } from '../schema';
import { roots, type Root } from './keys';

export type LiveEventType = LiveEvent['type'];

/** Which query areas each live event makes stale. */
export const liveInvalidations: Record<LiveEventType, readonly Root[]> = {
  'booking.created': [roots.ops, roots.bookings, roots.dashboard, roots.customers],
  'booking.confirmed': [roots.ops, roots.bookings, roots.dashboard, roots.payments],
  'booking.status_changed': [
    roots.ops,
    roots.bookings,
    roots.dashboard,
    roots.fleet,
    roots.customers,
    roots.credits,
  ],
  'booking.schedule_changed': [roots.ops, roots.bookings, roots.dashboard],
  'payment.succeeded': [
    roots.ops,
    roots.bookings,
    roots.payments,
    roots.finance,
    roots.dashboard,
    roots.invoices,
  ],
  'wallet.credit_request_created': [roots.credits, roots.bookings, roots.dashboard, roots.ops],
  'fleet.car_status_changed': [roots.fleet, roots.ops, roots.dashboard],
  'fleet.maintenance_due': [roots.fleet, roots.dashboard],
  'operations.no_show': [roots.ops, roots.bookings, roots.dashboard],
  'content.published': [roots.content, roots.public],
  'stream.resync': [],
};

export const LIVE_EVENT_TYPES = Object.keys(liveInvalidations) as LiveEventType[];

export function applyLiveEvent(qc: QueryClient, ev: Pick<LiveEvent, 'type'>): void {
  if (ev.type === 'stream.resync') {
    void qc.invalidateQueries({ refetchType: 'active' });
    return;
  }
  for (const root of liveInvalidations[ev.type]) {
    void qc.invalidateQueries({ queryKey: [root] });
  }
}

function parse(data: unknown): LiveEvent | null {
  if (typeof data !== 'string') return null;
  try {
    const v: unknown = JSON.parse(data);
    if (typeof v === 'object' && v !== null && 'type' in v) return v as LiveEvent;
  } catch {
    // ignore malformed frames
  }
  return null;
}

/**
 * Subscribes to the staff event stream (GET /api/v1/events) and invalidates
 * the matching queries, so Today, Calendar and badges update live. The
 * browser's EventSource reconnects on its own with Last-Event-ID.
 */
export function useLiveEvents(
  opts: { url?: string; enabled?: boolean; onEvent?: (ev: LiveEvent) => void } = {},
): void {
  const qc = useQueryClient();
  const { url = '/api/v1/events', enabled = true, onEvent } = opts;
  useEffect(() => {
    if (!enabled || typeof EventSource === 'undefined') return;
    const es = new EventSource(url, { withCredentials: true });
    const handler = (e: MessageEvent) => {
      const ev = parse(e.data);
      if (!ev) return;
      applyLiveEvent(qc, ev);
      onEvent?.(ev);
    };
    for (const t of LIVE_EVENT_TYPES) es.addEventListener(t, handler);
    es.addEventListener('message', handler);
    return () => {
      es.close();
    };
  }, [qc, url, enabled, onEvent]);
}
